import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Card,
    CardContent
} from '@mui/material';
import {
    Map as MapIcon,
    Launch as LaunchIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Global cache for geocoding results to avoid repeated API calls
const geocodingCache = new Map();

// Rate limiting: Track last request time to ensure 1 request per second max
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1100; // 1.1 seconds to be safe

const AddressMapPage = () => {
    const [address, setAddress] = useState('');
    const [coordinates, setCoordinates] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mapKey, setMapKey] = useState(0); // Force map re-render

    const geocodeAddress = useCallback(async (searchAddress) => {
        if (!searchAddress.trim()) {
            setError('Please enter an address');
            return;
        }

        // Check cache first
        const cacheKey = searchAddress.toLowerCase().trim();
        if (geocodingCache.has(cacheKey)) {
            const cachedResult = geocodingCache.get(cacheKey);
            if (cachedResult.coordinates) {
                setCoordinates(cachedResult.coordinates);
                setError('');
                setMapKey(prev => prev + 1); // Force map re-render
            } else {
                setError(cachedResult.error || 'Address not found on map');
                setCoordinates(null);
            }
            return;
        }

        setLoading(true);
        setError('');
        setCoordinates(null);

        try {
            // Rate limiting: Ensure we don't exceed 1 request per second
            const now = Date.now();
            const timeSinceLastRequest = now - lastRequestTime;
            if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
                const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
                await new Promise(resolve => setTimeout(resolve, waitTime));
            }
            lastRequestTime = Date.now();

            // Create AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            // Use OpenStreetMap Nominatim API for geocoding with proper headers
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}&limit=1`,
                {
                    signal: controller.signal,
                    headers: {
                        'User-Agent': 'PulseMembership/1.0 (Business Membership Application)', // Required by Nominatim policy
                    }
                }
            );
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                throw new Error(`Geocoding service error: ${response.status}`);
            }

            const data = await response.json();
            
            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                const coordinates = [parseFloat(lat), parseFloat(lon)];
                
                // Cache successful result
                geocodingCache.set(cacheKey, { coordinates });
                
                setCoordinates(coordinates);
                setMapKey(prev => prev + 1); // Force map re-render
            } else {
                // Cache failed result to avoid retrying
                geocodingCache.set(cacheKey, { error: 'Address not found on map' });
                setError('Address not found on map');
            }
        } catch (err) {
            console.error('Geocoding error:', err);
            
            let errorMessage = 'Unable to locate address on map';
            if (err.name === 'AbortError') {
                errorMessage = 'Map loading timed out. Please try again later.';
            } else if (err.message.includes('429')) {
                errorMessage = 'Too many map requests. Please wait a moment and try again.';
            } else if (err.message.includes('503') || err.message.includes('502')) {
                errorMessage = 'Map service temporarily unavailable. Please try again later.';
            }
            
            // Cache error to avoid immediate retry
            geocodingCache.set(cacheKey, { error: errorMessage });
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSearch = () => {
        geocodeAddress(address);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleGoogleMapsClick = () => {
        if (address.trim()) {
            const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
            window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const handleClear = () => {
        setAddress('');
        setCoordinates(null);
        setError('');
        setMapKey(prev => prev + 1);
    };

    return (
        <Box>
            <Typography variant="h4" component="h1" gutterBottom>
                <MapIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                Address Map
            </Typography>
            
            <Typography variant="body1" color="text.secondary" paragraph>
                Enter an address below to view its location on the map. You can also open the location in Google Maps.
            </Typography>

            {/* Address Input Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                            fullWidth
                            label="Enter Address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="e.g., 123 Main Street, Toronto, ON, Canada"
                            variant="outlined"
                            disabled={loading}
                            sx={{ flexGrow: 1 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={loading || !address.trim()}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                            sx={{ minWidth: 120 }}
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleClear}
                            disabled={loading}
                            sx={{ minWidth: 80 }}
                        >
                            Clear
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Loading Display */}
            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Searching for address...</Typography>
                </Box>
            )}

            {/* Map Section */}
            <Card>
                <CardContent sx={{ p: 0 }}>
                    {/* Map Header with Google Maps Link */}
                    {coordinates && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <MapIcon sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    Map Location
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<LaunchIcon />}
                                onClick={handleGoogleMapsClick}
                                sx={{ minWidth: 140 }}
                            >
                                Open in Google Maps
                            </Button>
                        </Box>
                    )}

                    {/* Map Container */}
                    <Box sx={{ height: 500, width: '100%' }}>
                        {coordinates ? (
                            <MapContainer
                                key={mapKey} // Force re-render when coordinates change
                                center={coordinates}
                                zoom={15}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <Marker position={coordinates}>
                                    <Popup>
                                        <div>
                                            <strong>Address Location</strong>
                                            <br />
                                            {address}
                                        </div>
                                    </Popup>
                                </Marker>
                            </MapContainer>
                        ) : (
                            <Box sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 2,
                                bgcolor: 'grey.50',
                                color: 'text.secondary'
                            }}>
                                <MapIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                                <Typography variant="h6" align="center">
                                    Enter an address to view the map
                                </Typography>
                                <Typography variant="body2" align="center">
                                    The map will appear here once you search for an address
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Instructions */}
            <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    <strong>Instructions:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                    • Enter a complete address including street, city, province/state, and country for best results
                    <br />
                    • The map uses OpenStreetMap data for location display
                    <br />
                    • Click "Open in Google Maps" to view the location in Google Maps with additional features
                    <br />
                    • Search results are cached to improve performance
                </Typography>
            </Box>
        </Box>
    );
};

export default AddressMapPage;
